import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { QFormControl, QMultiSelect, QSelect, QSelectPlaceholder, QText } from '@qualio/ui-components';
import { RootCause } from '../../../types';
import ItemWithIdAndNameLabel from './ItemWithIdAndNameLabel';
import { EMPTY_FILTER_VALUE } from '../../../types/qualityEvent';
import { emptyAwareFilterOption } from './emptyAwareFilterOption';
import { nativeCodeNameFilterOption } from './codeNameFilterOption';
import { CodeNameMultiSelect, CodeNameSelect } from './codeNameSelect';

export const RootCauseFilter = ({
  isLoadingRootCauses,
  rootCauses,
  isMultiSelect = false,
  isAdditionalEventListFiltersEnabled = false,
  isMultiSelectEventsListEnabled = false,
}: {
  isLoadingRootCauses: boolean;
  rootCauses: RootCause[];
  isMultiSelect?: boolean;
  isAdditionalEventListFiltersEnabled?: boolean;
  isMultiSelectEventsListEnabled?: boolean;
}) => {
  const { t } = useTranslation();

  const nativeRootCausesOptions = rootCauses.map((rootCause) => ({
    value: rootCause.id,
    label: rootCause.name,
    code: rootCause.displayId,
  }));

  const nativeMultiSelectRootCausesOptions: Array<{ value: string; label: React.ReactNode; code?: string }> = [
    ...(isAdditionalEventListFiltersEnabled
      ? [{ value: EMPTY_FILTER_VALUE, label: <em>{t('common.labels.emptyRootCauses')}</em> }]
      : []),
    ...nativeRootCausesOptions,
  ];

  const rootCausesOptions = rootCauses.map((rootCause) => ({
    value: rootCause.id,
    label: <ItemWithIdAndNameLabel itemName={rootCause.name} itemId={rootCause.displayId} setMinWidth={true} />,
  }));

  const multiSelectRootCausesOptions: Array<{ value: string; label: React.ReactNode }> = [
    ...(isAdditionalEventListFiltersEnabled
      ? [{ value: EMPTY_FILTER_VALUE, label: <em>{t('common.labels.emptyRootCauses')}</em> }]
      : []),
    ...rootCauses.map((rootCause) => ({
      value: rootCause.id,
      label: `${rootCause.displayId} - ${rootCause.name}`,
    })),
  ];

  const rootCauseFilter = useCallback((option: any, searchText: string) => {
    const rootCauseName = option.data.label.props.itemName;
    const rootCauseId = option.data.label.props.itemId;
    return !!(
      rootCauseName.toLowerCase().includes(searchText.toLowerCase()) ||
      rootCauseId.toLowerCase().includes(searchText.toLowerCase())
    );
  }, []);

  return (
    <QFormControl
      label={isMultiSelect ? t('common.labels.rootCauses') : t('common.labels.rootCause')}
      data-cy="root-cause-filter"
      w="300px"
    >
      {isMultiSelect ? (
        isMultiSelectEventsListEnabled ? (
          <Controller
            name={'rootcause_ids'}
            render={({ field: { onChange, value } }) => (
              <QMultiSelect
                options={nativeMultiSelectRootCausesOptions}
                aria-label="root-cause-filter"
                onChange={(selected) => onChange(selected.map((c) => c.value))}
                value={value ?? []}
                isLoading={isLoadingRootCauses}
                filterOption={nativeCodeNameFilterOption}
              >
                <QSelectPlaceholder>
                  <QText>{t('common.filters.filterByRootCause')}</QText>
                </QSelectPlaceholder>
              </QMultiSelect>
            )}
          />
        ) : (
          <Controller
            name={'rootcause_ids'}
            render={({ field: { onChange, value } }) => (
              <CodeNameMultiSelect
                options={multiSelectRootCausesOptions}
                aria-label="root-cause-filter"
                onChange={(val) => onChange(val.map((c) => c.value))}
                value={value ?? []}
                isLoading={isLoadingRootCauses}
                filterOption={emptyAwareFilterOption}
              >
                <QSelectPlaceholder>
                  <QText>{t('common.filters.filterByRootCause')}</QText>
                </QSelectPlaceholder>
              </CodeNameMultiSelect>
            )}
          />
        )
      ) : isMultiSelectEventsListEnabled ? (
        <Controller
          name={'rootcause_id'}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <QSelect
              {...field}
              options={nativeRootCausesOptions}
              filterOption={nativeCodeNameFilterOption}
              onChange={(changeObject) => {
                onChange(changeObject?.value ?? '');
              }}
              aria-label="root-cause-filter"
              isClearable={true}
              value={value ?? ''}
              isLoading={isLoadingRootCauses}
              key={`event-root-cause-${value}`}
            >
              <QSelectPlaceholder>
                <QText>{t('common.filters.filterByRootCause')}</QText>
              </QSelectPlaceholder>
            </QSelect>
          )}
        />
      ) : (
        <Controller
          name={'rootcause_id'}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <CodeNameSelect
              {...field}
              options={rootCausesOptions}
              filterOption={rootCauseFilter}
              onChange={(changeObject) => {
                onChange(changeObject?.value ?? '');
              }}
              aria-label="root-cause-filter"
              isClearable={true}
              value={value ?? ''}
              isLoading={isLoadingRootCauses}
              key={`event-root-cause-${value}`}
            >
              <QSelectPlaceholder>
                <QText>{t('common.filters.filterByRootCause')}</QText>
              </QSelectPlaceholder>
            </CodeNameSelect>
          )}
        />
      )}
    </QFormControl>
  );
};

export default RootCauseFilter;
